import type { Company } from '../types/company.types';

import { BackendRoute } from '../config/index';
import { ApiService } from '../services/index';
import { PageRoute } from '../types/page.types';

const COMPANIES_PAGE_SIZE = 500;

type UnfinishedCompanyCreationCandidate = Pick<
  Company,
  'id' | 'isActive' | 'isCompleted' | 'isEnterpriseRequested' | 'subscriptionOfferId'
>;

type GetCompaniesResponse = {
  data: UnfinishedCompanyCreationCandidate[];
  totalPages: number;
};

const getPathname = (path: string): string => path.split('?')[0] || path;

export const isUnfinishedCompanyCreation = (
  company: UnfinishedCompanyCreationCandidate,
): boolean => !company.isCompleted && !company.isEnterpriseRequested;

export const hasActiveCompany = (company: UnfinishedCompanyCreationCandidate): boolean =>
  company.isActive;

export const getUnfinishedCompanyCreationResumePath = (
  company: UnfinishedCompanyCreationCandidate,
): string =>
  company.subscriptionOfferId
    ? `${PageRoute.BILLING}/${company.id}`
    : `${PageRoute.COMPANY_SETUP}/${company.id}`;

const isCompanyCreationPath = (pathname: string): boolean =>
  pathname.startsWith(`${PageRoute.COMPANY_SETUP}/`) ||
  pathname.startsWith(`${PageRoute.BILLING}/`);

export const resolveUnfinishedCompanyCreationRedirect = (
  companies: UnfinishedCompanyCreationCandidate[],
  currentPath = '',
): null | string => {
  const pathname = getPathname(currentPath);
  const unfinished: UnfinishedCompanyCreationCandidate[] = [];

  for (const company of companies) {
    if (hasActiveCompany(company)) {
      return null;
    }
    if (isUnfinishedCompanyCreation(company)) {
      unfinished.push(company);
    }
  }

  if (unfinished.length === 0) {
    return null;
  }

  if (unfinished.some((company) => getUnfinishedCompanyCreationResumePath(company) === pathname)) {
    return null;
  }

  if (unfinished.length === 1) {
    return getUnfinishedCompanyCreationResumePath(unfinished[0]);
  }

  return pathname === PageRoute.MY_COMPANIES ? null : PageRoute.MY_COMPANIES;
};

export const resolveUnfinishedCompanySessionRedirect = async (
  currentPath: string,
): Promise<null | string> => {
  const pathname = getPathname(currentPath);
  if (isCompanyCreationPath(pathname)) {
    return null;
  }

  try {
    const { data } = await ApiService.get<GetCompaniesResponse>(
      { endpoint: BackendRoute.COMPANIES },
      {
        queryParams: {
          take: COMPANIES_PAGE_SIZE,
          skip: 0,
        },
      },
    );

    return resolveUnfinishedCompanyCreationRedirect(data, pathname);
  } catch {
    return null;
  }
};
